import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import { SPECIALTIES, SPECIALTY_IDS } from "config/specialtyConfig";
import { switchSpecialty } from "redux/Specialty/SpecialtyAction";
import { hasPaidAccess } from "config/subscriptionPlans";

const SEEN_KEY = "seenSpecialties";

const getSeenSpecialties = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || []; }
  catch { return []; }
};

const markSeen = (id) => {
  const seen = getSeenSpecialties();
  if (!seen.includes(id)) {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, id]));
  }
};

const SpecialtySwitcher = () => {
  const dispatch = useDispatch();
  const activeSpecialty = useSelector((state) => state.specialty.activeSpecialty);
  const userDetails = useSelector((state) => state.loginAuth.userDetails);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [seenList, setSeenList] = useState(getSeenSpecialties);

  const toggle = () => setDropdownOpen((prev) => !prev);

  const current = SPECIALTIES[activeSpecialty] || SPECIALTIES.pmr;

  // Mark active specialty as seen
  useEffect(() => {
    if (activeSpecialty && hasPaidAccess(userDetails, activeSpecialty)) {
      markSeen(activeSpecialty);
      setSeenList(getSeenSpecialties());
    }
  }, [activeSpecialty, userDetails]);

  const isNew = useCallback((id) => {
    if (id === activeSpecialty) return false;
    if (seenList.includes(id)) return false;
    return hasPaidAccess(userDetails, id);
  }, [activeSpecialty, seenList, userDetails]);

  const handleSwitch = (id) => {
    if (id !== activeSpecialty) {
      dispatch(switchSpecialty(id));
      if (hasPaidAccess(userDetails, id)) {
        markSeen(id);
        setSeenList(getSeenSpecialties());
      }
    }
  };

  const hasAnyNew = SPECIALTY_IDS.some(isNew);

  return (
    <div className="py-2">
      <Dropdown isOpen={dropdownOpen} toggle={toggle} className="w-100">
        <DropdownToggle
          caret
          className="w-100 d-flex align-items-center justify-content-between"
          color={current.color}
        >
          <span>
            <i className={current.icon + " mr-2"} />
            {current.name}
            {hasAnyNew && (
              <Badge color="success" pill className="ml-2" style={{ fontSize: '0.65rem' }}>NEW</Badge>
            )}
          </span>
        </DropdownToggle>
        <DropdownMenu className="w-100">
          {SPECIALTY_IDS.map((id) => {
            const spec = SPECIALTIES[id];
            const isActive = id === activeSpecialty;
            const showNew = isNew(id);
            return (
              <DropdownItem
                key={id}
                onClick={() => handleSwitch(id)}
                active={isActive}
                className="d-flex align-items-center"
              >
                <i className={spec.icon + " mr-2"} />
                {spec.name}
                {showNew && (
                  <Badge color="success" pill className="ml-auto" style={{ fontSize: '0.6rem' }}>NEW</Badge>
                )}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default SpecialtySwitcher;
